import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  PopoverBody,
  PopoverHeader,
  PopoverCloseButton,
  PopoverArrow,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  VStack,
  Popover,
  PopoverAnchor,
  useDisclosure,
  HStack,
  Text,
  Stack,
} from "@chakra-ui/react";
import { ConfirmAction, PerformerRow } from "./PerformerRow";
import { useContext, useState } from "react";
import { Performer } from "./LineupSection";
import { useLoading } from "src/Manager/hooks/form";
import AddManualArtistDialog, {
  AddManualArtistDialogContext,
  withAddManualArtistDialog,
} from "./AddManualArtistDialog";

const AddArtistInput = ({
  onSearch,
  searchResults,
  onConfirm,
  isLoading = false,
  isDisabled = false,
}: {
  onSearch: (query: string) => Promise<void>;
  onConfirm: ({
    name,
    image_url,
    soundcharts_id,
    profiles,
  }: {
    id?: string;
    headliner?: boolean;
    name: string;
    image_url?: string;
    soundcharts_id?: string;
    profiles: {
      platform: string;
      url: string;
    }[];
  }) => Promise<void>;
  searchResults: Performer[];
  isLoading?: boolean;
  isDisabled?: boolean;
}) => {
  const {
    isOpen: isAddManualArtistDialogOpen,
    open: openAddManualArtistDialog,
    close: closeAddManualArtistDialog,
  } = useContext(AddManualArtistDialogContext);
  const [query, setQuery] = useState("");
  const [showMore, setShowMore] = useState(false);
  const showShowMoreButton = searchResults.length > 5;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [confirmLoading, setConfirmLoading] =
    useLoading<Record<string, boolean>>();

  return (
    <VStack w="full">
      <AddManualArtistDialog
        isLoading={confirmLoading?.["manual"]}
        initialName={query}
        onSubmit={async (artistData) => {
          setConfirmLoading("manual", true);
          await onConfirm({
            name: artistData.name,
            image_url: artistData.image_url,
            //@ts-ignore
            profiles: artistData.profiles,
          });
          setConfirmLoading("manual", false);
          closeAddManualArtistDialog();
        }}
      />
      <Popover
        matchWidth
        isOpen={isOpen}
        onClose={onClose}
        onOpen={onOpen}
        closeOnBlur
      >
        <PopoverAnchor>
          <InputGroup size="lg">
            <Input
              placeholder="Search for the performer name to add to the lineup"
              size="lg"
              outline={"1px solid gray.200"}
              borderRadius="lg"
              borderTopRightRadius={"lg"}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onSearch(query).then(() => {
                    onOpen();
                  });
                }
              }}
              value={query}
              borderBottomRightRadius={"lg"}
              onChange={(e) => setQuery(e.target.value)}
              isDisabled={isLoading || isDisabled}
            />
            <InputRightElement>
              {isLoading ? (
                <Spinner />
              ) : (
                <IconButton
                  onClick={() => onSearch(query).then(() => onOpen())}
                  isDisabled={isDisabled || !query}
                  borderTopLeftRadius={0}
                  border={"1px solid gray.200"}
                  borderBottomLeftRadius={0}
                  size="lg"
                  borderRadius="lg"
                  icon={<SearchIcon color="gray.600" />}
                  aria-label="Search"
                />
              )}
            </InputRightElement>
          </InputGroup>
        </PopoverAnchor>
        <PopoverContent w="100%" minWidth={"fit-content"} zIndex={1000}>
          <PopoverBody w="100%" maxH="500px" overflowY="auto">
            <VStack mb={6} w="full" justifyContent="flex-start">
              {searchResults.slice(0, showMore ? 10 : 5).map(
                (s) =>
                  s && (
                    <Box
                      key={s.id}
                      p={2}
                      borderColor="gray.100"
                      borderWidth={1}
                      w="full"
                      borderRadius="xl"
                    >
                      <PerformerRow
                        platforms={s.profiles}
                        name={s.name}
                        id={s.id}
                        image={s.image_url}
                        actions={[
                          <ConfirmAction
                            name={"Confirm"}
                            key="confirm"
                            isLoading={confirmLoading?.[s.id]}
                            onClick={async () => {
                              setConfirmLoading(s.id, true);
                              await onConfirm({
                                name: s.name,
                                image_url: s.image_url,
                                soundcharts_id: s.soundcharts_id,
                                profiles: s.profiles || [],
                              });
                              setConfirmLoading(s.id, false);
                              setQuery("");
                              onClose();
                            }}
                          />,
                        ]}
                      />
                    </Box>
                  )
              )}
              <HStack
                fontSize={"sm"}
                w="full"
                justifyContent={"space-between"}
                p={2}
              >
                <Stack direction={["column", "row"]} w="full">
                  <Text>Don't see the artist you're looking for?</Text>
                  <Button
                    variant={"link"}
                    colorScheme={"blue"}
                    size={"sm"}
                    onClick={() => {
                      openAddManualArtistDialog();
                    }}
                  >
                    Add Manually
                  </Button>
                </Stack>

                {showShowMoreButton && !showMore ? (
                  <Button onClick={() => setShowMore(!showMore)}>
                    Show more
                  </Button>
                ) : null}
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </VStack>
  );
};
export default withAddManualArtistDialog(AddArtistInput);
